import {dataTestId} from '@hconnect/uikit'
import {Alert, HPToggleButton} from '@hconnect/uikit/src/lib2'
import {
  Box,
  Checkbox,
  FormControlLabel,
  Stack,
  TextField,
  useMediaQuery,
  useTheme
} from '@mui/material'
import {DatePicker} from '@mui/x-date-pickers'
import {AdapterMoment} from '@mui/x-date-pickers/AdapterMoment'
import {LocalizationProvider} from '@mui/x-date-pickers/LocalizationProvider'
import moment from 'moment-timezone'
import React from 'react'
import {Control, Controller, FieldErrors} from 'react-hook-form'
import {useTranslation} from 'react-i18next'

import {DAY_PERIODS} from '../constants/measurementTypes'
import {formatLabelWithUnit} from '../hooks/useStrengthMeasurementUnit'
import {StrengthTargetFormData} from '../types'

import {ControlledTextField} from '@settings/modules/common/components'

interface StrengthTargetFormFieldsProps {
  control: Control<StrengthTargetFormData>
  errors: FieldErrors<StrengthTargetFormData>
  isEdit: boolean
  unit: string | undefined
  createAnother: boolean
  showAtLeastOneValueError: boolean
  timezone: string
  onCreateAnotherChange: (checked: boolean) => void
}

export const StrengthTargetFormFields: React.FC<StrengthTargetFormFieldsProps> = ({
  control,
  errors,
  isEdit,
  unit,
  createAnother,
  showAtLeastOneValueError,
  timezone,
  onCreateAnotherChange
}) => {
  const {t} = useTranslation()
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))

  return (
    <Stack spacing={3}>
      {!isEdit && (
        <Box>
          <Controller
            name="dayPeriod"
            control={control}
            render={({field}) => (
              <HPToggleButton
                value={String(field.value)}
                onChange={(value) => field.onChange(Number(value))}
                backgroundMode="light"
                color="primary"
                options={DAY_PERIODS.map((period) => ({
                  value: String(period),
                  title: isMobile
                    ? `${period}D`
                    : t('materialsSettings.strengthTargets.dayPeriod', {count: period}),
                  dataTestId: `day_period_${period}`
                }))}
                {...dataTestId('day_period_selection')}
              />
            )}
          />
        </Box>
      )}

      {showAtLeastOneValueError && (
        <Alert
          accentColor="error"
          content={t('materialsSettings.strengthTargets.validation.atLeastOneValue')}
          data-test-id="form_validation_error"
        />
      )}

      <Stack direction={isMobile ? 'column' : 'row'} spacing={2}>
        <ControlledTextField
          control={control}
          formDataName="target"
          label={formatLabelWithUnit(t('materialsSettings.strengthTargets.target'), unit)}
          TextFieldProps={{
            type: 'number',
            inputProps: {
              step: 0.01,
              min: 1,
              max: 100,
              'data-test-id': 'target_number_input'
            },
            error: !!errors.target,
            helperText: errors.target?.message
          }}
          {...dataTestId('target_input')}
        />

        <ControlledTextField
          control={control}
          formDataName="lowerBound"
          label={formatLabelWithUnit(t('materialsSettings.strengthTargets.lowerBound'), unit)}
          TextFieldProps={{
            type: 'number',
            inputProps: {
              step: 0.01,
              min: 1,
              max: 100,
              'data-test-id': 'lower_bound_number_input'
            },
            error: !!errors.lowerBound,
            helperText: errors.lowerBound?.message
          }}
          {...dataTestId('lower_bound_input')}
        />
      </Stack>

      <Stack direction={isMobile ? 'column' : 'row'} spacing={2}>
        <ControlledTextField
          control={control}
          formDataName="upperBound"
          label={formatLabelWithUnit(t('materialsSettings.strengthTargets.upperBound'), unit)}
          TextFieldProps={{
            type: 'number',
            inputProps: {
              step: 0.01,
              min: 1,
              max: 100,
              'data-test-id': 'upper_bound_number_input'
            },
            error: !!errors.upperBound,
            helperText: errors.upperBound?.message
          }}
          {...dataTestId('upper_bound_input')}
        />

        <Controller
          name="validFrom"
          control={control}
          render={({field, fieldState}) => (
            <LocalizationProvider dateAdapter={AdapterMoment}>
              <DatePicker
                {...field}
                inputFormat="DD.MM.YYYY"
                label={t('materialsSettings.strengthTargets.validFrom')}
                value={field.value ? moment.tz(field.value, 'YYYY-MM-DD', timezone) : null}
                onChange={(value) => {
                  field.onChange(value ? moment(value).format('YYYY-MM-DD') : '')
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    fullWidth
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message}
                    InputProps={{
                      ...params.InputProps,
                      inputProps: {
                        ...params.inputProps,
                        'data-test-id': 'date_picker_input'
                      }
                    }}
                    sx={{
                      '& .MuiInputBase-root': {
                        height: (theme) => theme.spacing(7),
                        borderRadius: (theme) => theme.spacing(1),
                        boxShadow: 'none',
                        backgroundColor: (theme) => theme.palette.background.paper
                      }
                    }}
                    {...dataTestId('valid_from_input')}
                  />
                )}
              />
            </LocalizationProvider>
          )}
        />
      </Stack>

      <ControlledTextField
        control={control}
        formDataName="comment"
        label={t('materialsSettings.strengthTargets.comment')}
        TextFieldProps={{
          multiline: true,
          rows: 3,
          placeholder: t('materialsSettings.strengthTargets.commentPlaceholder'),
          inputProps: {
            'data-test-id': 'comment_textarea'
          }
        }}
        {...dataTestId('comment_input')}
      />

      {!isEdit && (
        <FormControlLabel
          control={
            <Checkbox
              checked={createAnother}
              onChange={(e) => onCreateAnotherChange(e.target.checked)}
              {...dataTestId('create_another_checkbox')}
            />
          }
          label={t('materialsSettings.strengthTargets.createAnotherTarget')}
          {...dataTestId('create_another_label')}
        />
      )}
    </Stack>
  )
}
